import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", hsn: "", unit: "MT" };

export default function Materials() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setItems((await api.get("/materials")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (editId) await api.put(`/materials/${editId}`, form);
    else await api.post("/materials", form);
    toast.success("Saved");
    setOpen(false); load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-4xl mx-auto" data-testid="materials-page">
      <PageHeader
        title="Materials"
        subtitle="Items you sell (sand, stone chips). HSN is required for GST-compliant tax invoices."
        action={
          <Button data-testid="add-material-btn"
            onClick={() => { setEditId(null); setForm(empty); setOpen(true); }}
            className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Material
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">Material</th>
              <th className="text-left px-3 py-3">HSN/SAC</th>
              <th className="text-left px-3 py-3">Unit</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`material-row-${m.id}`}>
                <td className="px-5 py-3 font-medium">
                  <Link to={`/ledger/material/${m.id}`} className="hover:text-brand-forest hover:underline">{m.name}</Link>
                </td>
                <td className="px-3 py-3 font-mono text-xs">{m.hsn || "—"}</td>
                <td className="px-3 py-3">{m.unit || "MT"}</td>
                <td className="px-5 py-3 text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditId(m.id); setForm({ ...empty, ...m }); setOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta"
                    onClick={async () => { if (confirm("Delete?")) { await api.delete(`/materials/${m.id}`); load(); } }}
                    data-admin-only="true">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Material</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="material-name" className="mt-1.5 h-10 rounded-sm bg-white" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>HSN / SAC</Label><Input className="mt-1.5 h-10 rounded-sm bg-white font-mono" value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></div>
            <div><Label>Unit</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-material-btn" className="rounded-sm bg-brand-forest text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
